import { PageHero, Eyebrow, Icon } from "@/components/site/ui";
import { Reveal, Stagger, StaggerItem } from "@/components/site/motion";
import { VALUES, TIMELINE, TEAM, IMAGES } from "@/data/content";

export default function APropos() {
  return (
    <>
      <PageHero
        eyebrow="À propos"
        titleLines={["Une histoire", "d'ambitions connectées"]}
        subtitle="Bridge Partners est né d'une conviction : rapprocher les expertises françaises des opportunités offertes par les Outre-mer et le continent africain, pour bâtir des projets solides et durables."
        image={IMAGES.business1}
      />

      <section className="bg-cream py-24 sm:py-32" data-testid="about-history">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-3xl">
              <img src={IMAGES.business2} alt="Équipe Bridge Partners en réunion" className="w-full h-[440px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Notre histoire</Eyebrow>
            <div className="mt-6 space-y-6">
              {[
                { t: "Notre genèse", d: "Notre entreprise est née de la volonté de rapprocher les expertises françaises des opportunités offertes par les territoires d'outre-mer et le continent africain. Nous accompagnons les organisations publiques et privées qui souhaitent développer leurs projets avec une approche stratégique, pragmatique et adaptée aux réalités locales." },
                { t: "Notre vision", d: "Être un partenaire de confiance pour les entreprises, les institutions publiques et les entrepreneurs qui souhaitent créer de la valeur et développer durablement leurs activités en France, en outre-mer et en Afrique." },
                { t: "Notre mission", d: "Accompagner nos clients dans leurs décisions stratégiques, le développement de leurs projets et leur croissance, en leur apportant une expertise de proximité, une vision internationale et une parfaite compréhension des enjeux locaux." },
              ].map((b) => (
                <div key={b.t}>
                  <h3 className="font-display text-xl text-navy">{b.t}</h3>
                  <p className="mt-2 text-anthracite font-body leading-relaxed">{b.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-night py-24" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow light>Nos valeurs</Eyebrow>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-medium text-white">Ce qui nous <em className="not-italic italic text-gold-gradient">définit</em></h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <StaggerItem key={v.title}>
                <div className="card-lumen rounded-2xl border border-gold/20 bg-navy/40 p-8 h-full hover:border-gold">
                  <Icon name={v.icon} size={28} className="text-gold" />
                  <h3 className="mt-5 font-display text-xl text-white">{v.title}</h3>
                  <p className="mt-2 text-sm text-white/70 font-body">{v.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-cream py-24 sm:py-32" data-testid="about-timeline">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Parcours</Eyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium text-navy">Les moments <em className="not-italic italic text-gold-gradient">clés</em></h2>
          </Reveal>
          <div className="mt-14 relative border-l-2 border-gold/30 pl-8 space-y-10">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.05}>
                <div className="relative">
                  <span className="absolute -left-[41px] top-1 h-4 w-4 rounded-full bg-gold ring-4 ring-cream" />
                  <span className="font-display text-2xl font-bold text-gold-gradient">{t.year}</span>
                  <h3 className="mt-1 font-display text-xl text-navy">{t.title}</h3>
                  <p className="mt-1 text-anthracite font-body">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream-dark py-24 sm:py-32" data-testid="about-team">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>L'équipe</Eyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium text-navy">Une équipe <em className="not-italic italic text-gold-gradient">pluridisciplinaire</em></h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {TEAM.map((m) => (
              <StaggerItem key={m.name}>
                <div className="card-lumen group overflow-hidden rounded-2xl border border-navy/10 bg-white hover:border-gold h-full" data-testid={`team-${m.name}`}>
                  <div className="relative h-56 overflow-hidden">
                    <img src={m.img} alt={m.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg text-navy">{m.name}</h3>
                    <p className="text-sm text-gold font-semibold">{m.role}</p>
                    <p className="mt-2 text-sm text-anthracite font-body">{m.bio}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {m.tags.map((tag) => (
                        <span key={tag} className="rounded-full bg-cream px-3 py-1 text-xs text-navy font-body">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
}
